/*
 * Copyright (c) University of Technology Sydney. All rights reserved.
 * See LICENSE file in the project root for full license information.
 *
 * Project Name: wpt-frontend.
 * Author: Tim Porritt
 * Created: 27/09/22
 */

import { useAssignment } from '@/components/contexts/AssignmentContext/useAssignment';
import CreateFutureWorkplanDialog from '@/components/FutureWorkplan/CreateFutureWorkplanDialog';
import config from '@/config';
import { ListResponse } from '@/wpt-lib/interfaces/listResponse';
import { Workplan, WorkplanState } from '@/wpt-lib/interfaces/Workplan';
import { useAsyncEffect } from '@/utils/hooks/useAsyncEffect';
import { useGetRequest } from '@/utils/useStatefulRequests';
import { Button, HStack, useDisclosure, ButtonProps } from '@chakra-ui/react';
import { range } from 'lodash';
import * as React from 'react';
import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Icons from '@/components/icons/Icons';

const CreateFutureWorkplanButton = (buttonProps: ButtonProps) => {
    const navigate = useNavigate();
    const { currentAssignment } = useAssignment();
    const { makeRequest, responseData: futureWorkplans } = useGetRequest<ListResponse<Workplan>>();

    const { isOpen, onClose, onOpen } = useDisclosure({ defaultIsOpen: false });

    const startYear = new Date().getFullYear() + 1;
    const prevYear = new Date().getFullYear() - 1;
    const fetchFutureWorkplans = useCallback(async () => {
        if (currentAssignment) {
            try {
                await makeRequest('/workplan', {
                    params: {
                        all: true,
                        'filters[year]': range(prevYear, startYear + config.constants.plannableFutureYears),
                        assignment: currentAssignment.id,
                        orderByColumn: 'year',
                        orderByOrientation: 'DESC',
                    },
                });
            } catch (e) {
                // handled by hook
            }
        }
    }, [currentAssignment]);
    useAsyncEffect(() => {
        void fetchFutureWorkplans();
    }, [currentAssignment]);

    const handleCreate = useCallback((workplan: Workplan) => {
        navigate(`/workplan/${workplan.id}/1`);
    }, []);

    if (!futureWorkplans) {
        return null;
    }

    const yearsToCreate = range(prevYear, startYear + config.constants.plannableFutureYears)
        .filter((y) => !futureWorkplans.items.find((w) => w.year === y && w.state !== WorkplanState.HISTORICAL))
        .map((y) => `${y}`);

    if (yearsToCreate.length === 0) {
        return null;
    }

    return (
        <HStack>
            <CreateFutureWorkplanDialog
                isOpen={isOpen}
                onClose={onClose}
                onCreate={handleCreate}
                years={yearsToCreate}
            />
            {!currentAssignment?.inactive && (
                <Button
                    leftIcon={<Icons.AddIcon />}
                    variant="outline"
                    whiteSpace="normal"
                    onClick={onOpen}
                    size="md"
                    rounded="none"
                    {...buttonProps}
                >
                    Add Workplan
                </Button>
            )}
        </HStack>
    );
};

export default CreateFutureWorkplanButton;
